import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { BookCallButton } from "@/context/BookCall";
import AgentRun from "@/components/AgentRun";
import { EngagementPath, RoiSection, CtaBanner } from "@/components/Blocks";
import { CAPABILITIES } from "@/lib/data";

export default function AiAgents() {
    return (
        <>
            <Seo
                title="AI agents & automation"
                description="Autonomous AI agents that classify, extract, decide, and act on unstructured inbound work — integrated with your CRM, ERP, and ticketing tools, deployed in your private environment."
                path="/ai-agents"
            />

            <Section className="pt-16 md:pt-24 pb-8">
                <Reveal>
                    <Eyebrow>AI agents & automation</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                    <h1 className="mt-6 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
                        Agents that read the work, and do it
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-lg md:text-xl text-pa-ink-500 max-w-2xl leading-relaxed">
                        An autonomous agent takes unstructured inbound work,
                        understands what it is, pulls out the details a person
                        would have to retype, acts through your own systems, and
                        returns a result. Built around your workflows, deployed
                        in your environment.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-8">
                        <BookCallButton testId="agents-book-call" />
                    </div>
                </Reveal>
            </Section>

            <Section className="pt-0">
                <AgentRun />
            </Section>

            {/* Capabilities — descriptive, not packaged */}
            <Section className="bg-pa-blue-050">
                <Reveal>
                    <Eyebrow>In scope</Eyebrow>
                    <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
                        Whatever the outcome requires, delivered by one team
                    </h2>
                    <p className="mt-5 text-lg text-pa-ink-500 max-w-2xl">
                        Most AI vendors hand back a model and leave you to wire
                        it into reality. Phoebus builds the whole path from the
                        business problem to the running system — including the
                        unglamorous integration work.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-4 md:grid-cols-3">
                    {CAPABILITIES.map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.05}>
                            <div className="group h-full bg-white border border-pa-line p-6 cut-tr hover:border-pa-blue-600 transition-colors">
                                <div className="h-9 w-9 bg-pa-blue-600 cut-tr-sm mb-4" />
                                <h3 className="font-display text-xl font-bold leading-snug">
                                    {c.title}
                                </h3>
                                <p className="mt-2 text-pa-ink-500 leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* Private deployment — dark */}
            <Section dark>
                <div className="grid gap-10 md:grid-cols-[1fr_1fr] md:gap-16">
                    <Reveal>
                        <Eyebrow light>Private / VPC deployment</Eyebrow>
                        <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.08]">
                            Your data stays inside your walls
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-lg text-pa-blue-050/80 leading-relaxed">
                            Smaller, purpose-trained models can run inside your
                            own private environment, with monitoring and
                            analytics you control. Sensitive data does not have
                            to leave your infrastructure or route through shared
                            public APIs — a headline benefit for regulated GCC
                            enterprises, not a footnote.
                        </p>
                    </Reveal>
                </div>
            </Section>

            {/* Confidential agentic case — strictly anonymised */}
            <Section>
                <Reveal>
                    <Eyebrow>Enterprise Query Automation</Eyebrow>
                    <div className="mt-6 max-w-3xl bg-white border border-pa-line p-8 md:p-10 cut-tr-lg">
                        <p className="font-display text-2xl md:text-3xl font-bold leading-snug text-pa-ink-900">
                            An operations team was handling 800–1,000 inbound
                            queries per day across email and messaging, causing
                            delays and inconsistent responses.
                        </p>
                        <p className="mt-5 text-lg text-pa-ink-500 leading-relaxed">
                            Phoebus AI built an AI agent that classifies query
                            intent, extracts key details from unstructured
                            messages, and integrates with the client's CRM and
                            internal tools via API — deployed on AWS with
                            monitoring and analytics.
                        </p>
                    </div>
                </Reveal>
            </Section>

            <EngagementPath dark />
            <RoiSection />
            <CtaBanner />
        </>
    );
}
